// [PURPOSE]: Tutorial module definitions — all step data for the contextual tutorial system
// [WHY]: Data-driven approach keeps tutorial content separate from rendering logic
// [INTEGRATION]: Consumed by TutorialContext to drive TutorialSpotlight positioning and flow
// [CREATED]: February 28, 2026 — Contextual Tutorial System redesign

// ========================================
// STEP TYPES
// ========================================
// 'info'   — Speech bubble with Next/Skip. User reads, clicks Next.
// 'action' — Speech bubble visible, Next disabled. Watches for user interaction, auto-advances.
// 'welcome' — Special centered modal (no spotlight). Used once after app open.

// ========================================
// WELCOME MODULE (auto-shows on first open)
// ========================================
export const WELCOME_MODULE = {
  id: 'welcome',
  title: 'Welcome',
  // [NOTE]: Welcome uses WelcomePopup component, not spotlight steps
  // It's tracked separately — completing it marks 'welcome' as done
};

// ========================================
// CHARACTER CREATION MODULE (~12 steps)
// ========================================
// [DESIGN]: Each wizard page gets ONE info step explaining all choices on that page,
// then ONE action step to click the wizard's Next button. This lets the user make all
// their selections at their own pace before advancing. Only the name input and wizard
// navigation buttons use action detection — everything else is read-and-advance.
// [REDESIGN Phase 2B]: Removed all "click Next" action steps.
// Each wizard page now gets ONE info step. When the user clicks Next in the tutorial panel,
// the tutorial advances to the next page's step (which uses waitForTarget to wait for render).
// If the user clicks the wizard's Next button first, the tutorial auto-advances when it detects
// the current target has disappeared. No more double-clicking, no more darkening content
// the user still needs to interact with.
export const CHARACTER_CREATION_MODULE = {
  id: 'character-creation',
  title: 'Character Creation',
  steps: [
    // === WIZARD PAGE 1: Name & Alignment ===
    {
      id: 'cc-name',
      target: '[data-tutorial="wizard-name-input"]',
      title: 'Name Your Hero',
      message: 'Every legend begins with a name. This is the only required field on this page. Type your character\'s name, then click Next when you\'re ready.',
      type: 'info',
    },
    {
      id: 'cc-page1-info',
      target: '[data-tutorial="wizard-alignment"]',
      title: 'Alignment (Optional)',
      message: 'Nine alignments from Lawful Good to Chaotic Evil. This is completely optional and has no mechanical effect — it\'s a roleplaying guide. Skip it or pick one that fits your character concept.',
      type: 'info',
      waitForTarget: true,
    },

    // === WIZARD PAGE 2: Race & Subrace ===
    {
      id: 'cc-page2-info',
      target: '[data-tutorial="wizard-race-select"]',
      title: 'Pick Your Race',
      message: 'Each race card shows ability score bonuses, speed, and special traits. Pick a race, then scroll down — some races have subraces with additional bonuses (like Hill vs Mountain Dwarf), and some grant extra language or skill choices.',
      type: 'info',
      waitForTarget: true,
    },
    {
      id: 'cc-subrace-info',
      target: '[data-tutorial="wizard-race-select"]',
      title: 'Subraces & Special Choices',
      message: 'If your race has subraces, they appear below after selection. Variant Human is special — instead of +1 to all stats, you get +1 to two stats, a bonus skill, and a free feat at level 1. Check for bonus language pickers too.',
      type: 'info',
      optional: true,
      waitForTarget: true,
    },

    // === WIZARD PAGE 3: Class & Skills ===
    {
      id: 'cc-page3-info',
      target: '[data-tutorial="wizard-class-select"]',
      title: 'Choose Your Class',
      message: 'Your class defines your combat style, abilities, and role. Each card shows the hit die (HP per level), primary ability, and saving throw proficiencies. Scroll down after picking to choose skill proficiencies, cantrips, and spells if your class grants them.',
      type: 'info',
      waitForTarget: true,
    },
    {
      id: 'cc-skills-info',
      target: '[data-tutorial="wizard-class-select"]',
      title: 'Skill Proficiency Selection',
      message: 'Each class lets you pick a limited number of skills (e.g., Rogue picks 4, Fighter picks 2). Checkboxes disable once you\'ve selected your maximum. If a skill conflicts with your background, the system detects it on the next page.',
      type: 'info',
      optional: true,
      waitForTarget: true,
    },

    // === WIZARD PAGE 4: Background ===
    {
      id: 'cc-page4-info',
      target: '[data-tutorial="wizard-background"]',
      title: 'Your Background',
      message: 'Backgrounds grant 2 skill proficiencies, tool proficiencies, and a feature. If a background skill conflicts with your class skill, a yellow warning appears with a dropdown to pick a replacement. Check tool pickers too — some backgrounds let you choose.',
      type: 'info',
      waitForTarget: true,
    },

    // === WIZARD PAGE 5: Ability Scores ===
    {
      id: 'cc-page5-info',
      target: '[data-tutorial="wizard-ability-scores"]',
      title: 'Ability Scores',
      message: 'Three methods: Standard Array (15/14/13/12/10/8 — simplest, no randomness), Point Buy (27 points, scores 8-15, full control), or Roll 4d6 Drop Lowest (click dice icons to roll each score — most random but most exciting). Racial bonuses are added automatically.',
      type: 'info',
      waitForTarget: true,
    },

    // === WIZARD PAGE 6: Equipment ===
    {
      id: 'cc-page6-info',
      target: '[data-tutorial="wizard-equipment"]',
      title: 'Starting Equipment',
      message: 'Radio buttons let you pick between equipment options (e.g., longsword OR any martial weapon). Starter packs bundle common items. Alternatively, "Roll for Starting Gold" gives you random gold to buy your own gear — useful for experienced players who want full control.',
      type: 'info',
      waitForTarget: true,
    },

    // === WIZARD PAGE 7: Personality & Appearance ===
    {
      id: 'cc-page7-info',
      target: '[data-tutorial="wizard-personality"]',
      title: 'Appearance & Personality',
      message: 'All fields here are optional. Click the dice icon next to any field to roll a random result from your background\'s tables, or click "Roll All" to fill everything at once. Personality traits, ideals, bonds, and flaws are great roleplaying hooks but have no mechanical effect.',
      type: 'info',
      waitForTarget: true,
    },

    // === WIZARD PAGE 8: Review & Create ===
    {
      id: 'cc-finish',
      target: '[data-tutorial="wizard-finish-btn"]',
      title: 'Bring Them to Life!',
      message: 'Review your choices above. When you\'re satisfied, click the Create Character button. Don\'t worry — everything can be changed later from the character sheet.',
      type: 'action',
      waitFor: { event: 'click' },
      waitForTarget: true,
    },
  ],
};

// ========================================
// TAB TUTORIAL MODULES (prompted on first visit)
// ========================================

// ========================================
// CHARACTER SHEET MODULE (NEW — Phase 2B)
// ========================================
// [PURPOSE]: Introduces the main character tab — where users spend 70%+ of their time
// [WHY]: This is the first tab users see after character creation. No tutorial existed before.
export const CHARACTER_MODULE = {
  id: 'character',
  title: 'Character Sheet Tour',
  promptMessage: 'Want a quick tour of your character sheet?',
  steps: [
    {
      id: 'char-identity',
      target: '[data-tutorial="character-identity"]',
      title: 'Your Character',
      message: 'This is your character sheet — the hub for everything about your hero. Your name, race, class, and level are all shown here. Click the portrait area to add character art.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'char-vitals',
      target: '[data-tutorial="character-vitals"]',
      title: 'Vital Stats',
      message: 'HP, AC, and Speed — the three numbers you check most in combat. Click your current HP to adjust it after taking damage or healing. Your AC and Speed update automatically from equipment and effects.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'char-abilities',
      target: '[data-tutorial="character-abilities"]',
      title: 'Ability Scores',
      message: 'Your six core abilities and their modifiers. These drive almost everything — skill checks, saving throws, attack rolls, and spell DCs. Click any score to adjust it manually.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'char-features',
      target: '[data-tutorial="character-features"]',
      title: 'Class Features',
      message: 'Your class features and limited-use resources appear here. Track uses of abilities like Action Surge, Rage, Ki Points, or Channel Divinity. They reset on short or long rests depending on the feature.',
      type: 'info',
      position: 'bottom',
      optional: true,
      waitForTarget: true,
    },
    {
      id: 'char-conditions',
      target: '[data-tutorial="character-conditions"]',
      title: 'Conditions',
      message: 'Track active conditions like Poisoned, Frightened, or Concentrating. Conditions automatically affect your rolls and stats where applicable.',
      type: 'info',
      position: 'bottom',
      optional: true,
      waitForTarget: true,
    },
    {
      id: 'char-senses',
      target: '[data-tutorial="character-senses"]',
      title: 'Senses & Movement',
      message: 'Your movement speed, darkvision range, passive perception, and other senses. These update automatically from racial traits, spells, and equipment.',
      type: 'info',
      position: 'bottom',
      optional: true,
      waitForTarget: true,
    },
  ],
};

// ========================================
// SHOP MODULE (EXPANDED — P2.10, Apr 29 2026)
// ========================================
// [PURPOSE]: Introduces the shop system — browsing, buying, and generating shops
// [WHY]: User report — "the shop tutorial isnt helpful, doesnt offer enough step
//        explanations, and closes after I hit next the very first time."
//        Pre-fix module had 3 steps; step 3 was optional with a target
//        ([data-tutorial="shop-generate"]) that only renders for premium /
//        non-just-inventory users. TutorialSpotlight auto-skipped it after 1.5s
//        when the target was missing, ending the tutorial after step 2 Next.
// [TARGET STRATEGY]: Every step now has an always-rendered fallback in its
//        comma-separated CSS selector. The first selector is the ideal element
//        to highlight; the fallback ([data-tutorial="shop-tutorial-anchor"] or
//        [data-tutorial="shop-browse"]) is a guaranteed always-rendered anchor
//        injected into the ShopTab outer container. No step is `optional` —
//        the auto-skip-on-missing-target failure mode is eliminated by design.
export const SHOP_MODULE = {
  id: 'shop',
  title: 'Shop Tour',
  promptMessage: 'Want a quick tour of the shop system?',
  steps: [
    {
      id: 'shop-orientation',
      target: '[data-tutorial="shop-header"], [data-tutorial="shop-browse"]',
      title: 'Welcome to the Shop',
      message: 'You\'re on the Shop tab — your hub for buying gear with the gold you\'ve earned. There are two ways to get a shop: pick a pre-built bundled shop (fast, DM-curated), or generate a randomized one from D&D shop tables. Either way, items you buy go straight into your inventory and the gold is deducted automatically.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'shop-generate-panel',
      target: '[data-tutorial="shop-generate"], [data-tutorial="shop-tutorial-anchor"]',
      title: 'Building or Loading a Shop',
      message: 'This panel is where shops come from. Generate a randomized shop by type and size, or import a shop JSON file someone else exported. Fill in the shop name and shopkeeper if you want flavor — they only affect display, not what\'s for sale.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'shop-type',
      // [PHASE B / #005 — B3-5]: Dropped `[data-tutorial="shop-bundled"]`
      // from the target chain — that anchor lived on the Premium "Or load
      // a bundled shop" dropdown that has been removed. Tutorial falls
      // back to the always-rendered shop-tutorial-anchor.
      target: '[data-tutorial="shop-shop-type"], [data-tutorial="shop-tutorial-anchor"]',
      title: 'Shop Type & Settlement Size',
      message: 'The Shop Type dropdown picks what kind of merchant you\'re visiting — Blacksmith, Apothecary, Tavern, and 30+ specialty homebrew options. Shop Size controls inventory count: Small (4 items), Medium (10), Large (17), or Massive (27+). Bigger shops carry more variety and a higher chance of rare items.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'shop-generate-action',
      target: '[data-tutorial="shop-generate-button"], [data-tutorial="shop-generate-actions"], [data-tutorial="shop-tutorial-anchor"]',
      title: 'Generate or Import',
      message: 'Click Generate Shop to roll a fresh randomized inventory based on your type and size. Or use Import File to load a shop someone else exported (a JSON file). The generation mode radio lets you switch between D&D roll-table generation and converting your character\'s inventory into a shop.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'shop-browse-items',
      target: '[data-tutorial="shop-display"], [data-tutorial="shop-buy"], [data-tutorial="shop-tutorial-anchor"]',
      title: 'Browsing the Shelves',
      message: 'Once a shop loads, items appear here with name, weight, and price. The price you see is base value times the shop\'s markup (default 150% — about right for a small town; cities run lower, frontier hamlets run higher). Items you can\'t afford show a red price; affordable ones show yellow. Your purse displays at the top so you always know where you stand.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'shop-buy-flow',
      target: '[data-tutorial="shop-display"], [data-tutorial="shop-buy"], [data-tutorial="shop-tutorial-anchor"]',
      title: 'Buying Items',
      message: 'Click the green Buy button next to any item to purchase it. The gold is deducted in the smartest denomination split (e.g., 47gp could come from 4pp + 7gp), the item lands in your inventory grid, and the shop\'s stock updates. Out of gold? The button turns grey and reads "Can\'t Afford" — no surprise overdrafts.',
      type: 'info',
      position: 'bottom',
    },
  ],
};

export const INVENTORY_MODULE = {
  id: 'inventory',
  title: 'Inventory Tour',
  promptMessage: 'Want a quick tour of your inventory grid?',
  steps: [
    {
      id: 'inv-grid',
      target: '[data-tutorial="inventory-grid"]',
      title: 'Your Inventory Grid',
      message: 'A physical grid where items occupy slots based on their size (a dagger is 1x1, a greatsword is 1x4). Drag items to rearrange them. Items snap to the grid — if it doesn\'t fit, try rotating or moving other items.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'inv-add',
      target: '[data-tutorial="inventory-add-item"]',
      title: 'Adding Items',
      message: 'Click the + button to open the Add Item dialog. Search the full D&D item database, browse by category, or build custom magic items with enchantments, charges, and granted spells. Items are auto-placed in the first available space.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'inv-interactions',
      target: '[data-tutorial="inventory-grid"]',
      title: 'Interacting with Items',
      message: 'Double-click any item to open its edit dialog. Right-click (or long-press on mobile) for a context menu with edit, move, split stacks, duplicate, and delete. Items with a purple glow are attuned magic items.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'inv-mobile-mode',
      target: '[data-tutorial="inventory-mobile-mode"]',
      title: 'Mobile Interaction Mode',
      message: 'On mobile, choose between Tap Mode (tap to select, tap destination to place) or Drag Mode (touch-drag items directly). Change this in Settings > Mobile Controls. Tap mode is easier on small screens.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'inv-currency',
      target: '[data-tutorial="inventory-currency"]',
      title: 'Your Gold Pouch',
      message: 'All five D&D denominations: Copper (CP), Silver (SP), Electrum (EP), Gold (GP), and Platinum (PP). Click any amount to edit it directly. Gold is auto-deducted when you purchase items from the shop or Add Item dialog.',
      type: 'info',
      position: 'top',
    },
    {
      id: 'inv-equip',
      target: '[data-tutorial="inventory-equip-area"]',
      title: 'Equipment Slots',
      message: 'Drag items to equipment slots to equip them — this auto-updates your AC, attack bonuses, and other stats. A crystal icon means the item is attuned; a lock icon means it requires attunement (toggle in the edit dialog).',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'inv-containers',
      target: '[data-tutorial="inventory-containers"]',
      title: 'Containers & Bags',
      message: 'Double-click a container (backpack, pouch, chest) to open its sub-grid. Magical containers like Bag of Holding show weight reduction. Containers cannot be nested inside other containers.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'inv-context',
      target: '[data-tutorial="inventory-grid"]',
      title: 'Right-Click Menu',
      message: 'Right-click (or long-press on mobile) any item for the full context menu: Edit properties, Move to container, Split stacks (for stackable items like arrows), Duplicate, or Delete.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'inv-weight',
      target: '[data-tutorial="inventory-weight"]',
      title: 'Carry Weight',
      message: 'Your carry capacity is Strength x 15 lbs. Exceeding this causes encumbrance, which reduces your speed. Magical containers (Bag of Holding, Handy Haversack) reduce the weight of items stored inside.',
      type: 'info',
      position: 'top',
      optional: true,
    },
    {
      id: 'inv-attunement',
      target: '[data-tutorial="inventory-grid"]',
      title: 'Attunement Limit',
      message: 'You can attune to a maximum of 3 magic items at once (D&D 5e rule). Attuned items glow purple. To attune/un-attune, open the item\'s edit dialog and toggle the attunement checkbox. Some items require attunement to activate their magical properties.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
  ],
};

export const SPELLBOOK_MODULE = {
  id: 'spellbook',
  title: 'Spellbook Tour',
  promptMessage: 'Want a quick tour of the spellbook?',
  steps: [
    {
      id: 'spell-list',
      target: '[data-tutorial="spellbook-list"]',
      title: 'Your Spellbook',
      message: 'All your known spells organized by level in collapsible sections. Click a level header to expand/collapse. Use the checkbox next to each spell to prepare it for the day (if your class requires preparation).',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'spell-slots',
      target: '[data-tutorial="spellbook-slots"]',
      title: 'Spell Slot Tracker',
      message: 'Each green dot is an available spell slot. Click a green dot to spend it (turns red). Click a red dot to recover it. Your class and level determine how many slots you have at each spell level.',
      type: 'info',
      position: 'top',
    },
    {
      id: 'spell-manual',
      target: '[data-tutorial="spellbook-manual-override"]',
      title: 'Manual Slot Override',
      message: 'Check "Manual Override" to set custom max slots with +/- buttons. Useful for magic items that grant extra slots, or homebrew rules. Leave unchecked for auto-calculated slots based on class/level.',
      type: 'info',
      position: 'top',
      optional: true,
    },
    {
      id: 'spell-prepared',
      target: '[data-tutorial="spellbook-prepared"]',
      title: 'Prepared Spells',
      message: 'Wizard, Cleric, Druid, and Paladin must prepare spells each morning — your limit is level + spellcasting modifier. Check the boxes in your spell list to prepare them. Bard, Sorcerer, Warlock, and Ranger always have their spells ready.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'spell-cast',
      target: '[data-tutorial="spellbook-cast"]',
      title: 'Casting Spells',
      message: 'Click the Cast button to use a spell. The system checks if you have available slots and (for prepared casters) if the spell is prepared. Cantrips are always free to cast. Damage dice are rolled automatically.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'spell-add',
      target: '[data-tutorial="spellbook-add"]',
      title: 'Adding Spells',
      message: 'Browse the full 343-spell D&D database filtered by your class. Select multiple spells to learn at once. Your class determines which spell lists you can access.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'spell-custom',
      target: '[data-tutorial="spellbook-custom"]',
      title: 'Custom Spells',
      message: 'Build homebrew spells that fully integrate with the casting system — damage dice, upcasting, saving throws, and more. Custom spells appear alongside official ones in your spellbook.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'spell-export',
      target: '[data-tutorial="spellbook-export"]',
      title: 'Export & Import',
      message: 'Export your spellbook as a JSON file for backup or sharing. Import brings spells back — choose "Replace" to overwrite or "Cancel" on the prompt to merge without losing existing spells.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'spell-scribe',
      target: '[data-tutorial="spellbook-scribe"]',
      title: 'Scribe Spell Scrolls',
      message: 'Craft spell scrolls from your prepared spells. Requires gold and time based on spell level (per DMG rules). Scrolls become inventory items that anyone can use — great for sharing spells with the party.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'spell-long-rest',
      target: '[data-tutorial="spellbook-long-rest"]',
      title: 'Quick Long Rest',
      message: 'Shortcut to restore all spell slots without leaving the spellbook tab. For a full long rest (HP, hit dice, features), use the Rest tab instead.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
  ],
};

export const SKILLS_MODULE = {
  id: 'skills',
  title: 'Skills Tour',
  promptMessage: 'Want a quick tour of skills & proficiencies?',
  steps: [
    {
      id: 'skills-list',
      target: '[data-tutorial="skills-list"]',
      title: 'Skills & Proficiencies',
      message: 'All 18 D&D skills grouped by ability score. Green highlight = proficient (adds your proficiency bonus). Each row shows the total modifier. Expertise (double proficiency) shows a special badge.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'skills-roll',
      target: '[data-tutorial="skills-roll"]',
      title: 'Advantage & Disadvantage',
      message: 'Toggle between Normal, Advantage (roll 2d20 take highest), and Disadvantage (roll 2d20 take lowest) before rolling. The toggle applies to your next roll and shows both dice results.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'skills-dc',
      target: '[data-tutorial="skills-dc"]',
      title: 'DC Check & Inspiration',
      message: 'Enter an optional DC (Difficulty Class) before rolling. If you fail by a close margin, the system prompts you to use Inspiration for a reroll — a helpful reminder so you never forget.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'skills-click-roll',
      target: '[data-tutorial="skills-list"]',
      title: 'Rolling Skill Checks',
      message: 'Click any skill row to roll d20 + modifier. The result shows a full breakdown: base roll, ability mod, proficiency bonus, and any conditional bonuses. Natural 20s and 1s are highlighted.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'skills-tools',
      target: '[data-tutorial="skills-tools-tab"]',
      title: 'Tool Proficiencies',
      message: 'The Tools tab shows your tool proficiencies. Each tool uses a specific ability score (Thieves\' Tools = DEX, Herbalism Kit = WIS). Click to roll just like skill checks.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'skills-languages',
      target: '[data-tutorial="skills-languages-tab"]',
      title: 'Languages',
      message: 'View all languages your character speaks. Includes 15+ standard D&D languages plus custom entries. Languages are granted by race, background, and some class features.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'skills-jack',
      target: '[data-tutorial="skills-list"]',
      title: 'Passive Bonuses',
      message: 'If you have Jack of All Trades (Bard feature), a badge appears and half your proficiency bonus auto-applies to non-proficient checks. Other passive bonuses from feats and items are factored in automatically.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'skills-saves',
      target: '[data-tutorial="skills-list"]',
      title: 'Saving Throws',
      message: 'Saving throws are managed on the Character tab under your ability scores. Your class grants proficiency in 2 saving throw types (shown in green). Active conditions like Poisoned or Frightened auto-apply disadvantage where the rules require it.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
  ],
};

export const FEATS_MODULE = {
  id: 'feats',
  title: 'Feats Tour',
  promptMessage: 'Want a quick tour of the feats system?',
  steps: [
    {
      id: 'feats-list',
      target: '[data-tutorial="feats-list"]',
      title: 'Your Feats & Features',
      message: 'Feats are special abilities earned at certain levels (typically 4, 8, 12, 16, 19). Each section is collapsible — click to expand and see the full description, choices you\'ve made, and any granted abilities.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'feats-add',
      target: '[data-tutorial="feats-add"]',
      title: 'Adding Official Feats',
      message: 'Browse 42+ SRD feats. Prerequisites are shown on each card. Some feats prompt for choices when added — like Magic Initiate (pick a class) or Skilled (pick 3 proficiencies). Feats from Tasha\'s and Xanathar\'s appear when those sources are enabled.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'feats-prereqs',
      target: '[data-tutorial="feats-list"]',
      title: 'Prerequisites & Validation',
      message: 'The system auto-checks ability score minimums, proficiency requirements, and class restrictions. A warning appears if you don\'t meet prerequisites — you can still add the feat, but you\'ll know it\'s technically invalid.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'feats-effects',
      target: '[data-tutorial="feats-list"]',
      title: 'Automatic Feat Effects',
      message: 'Many feats auto-apply their bonuses: Mobile adds +10 speed, Tough adds +2 HP per level, Heavy Armor Master reduces damage. These register with the effect system and update your stats immediately — no manual tracking needed.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'feats-active',
      target: '[data-tutorial="feats-list"]',
      title: 'Active Abilities & Toggles',
      message: 'Look for lightning bolt icons (active abilities tracked per rest) and shield icons (toggleable effects). Active abilities show remaining uses and reset on short or long rests. Toggle effects can be switched on/off.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'feats-custom',
      target: '[data-tutorial="feats-custom"]',
      title: 'Custom Feat Creator',
      message: 'Build homebrew feats with auto-applying effects — ability score increases, speed bonuses, proficiency grants, and more. Custom feats integrate with the effect system identically to official ones.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'feats-remove',
      target: '[data-tutorial="feats-list"]',
      title: 'Managing Feats',
      message: 'Click the X button to remove a feat — its effects are immediately removed from your stats. Race and class features are locked and can\'t be removed (they\'re part of your character\'s identity).',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
  ],
};

export const REST_MODULE = {
  id: 'rest',
  title: 'Resting Tour',
  promptMessage: 'Want a quick tour of the rest system?',
  steps: [
    {
      id: 'rest-types',
      target: '[data-tutorial="rest-panel"]',
      title: 'Short & Long Rests',
      message: 'Short rest (1 hour): spend Hit Dice to heal, recover some class features. Long rest (8 hours): fully restore HP, all spell slots, and most features. Both are essential to survival.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'rest-hit-dice',
      target: '[data-tutorial="rest-hit-dice"]',
      title: 'Hit Dice Healing',
      message: 'Click to spend a Hit Die during short rest. Each die rolls 1dX + CON modifier, where X depends on your class: d6 (Sorcerer/Wizard), d8 (Bard/Cleric/Rogue), d10 (Fighter/Ranger), d12 (Barbarian). Multiclass characters see each die type separately.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'rest-hd-recovery',
      target: '[data-tutorial="rest-hit-dice"]',
      title: 'Recovering Hit Dice',
      message: 'Long rests recover half your total Hit Dice (rounded down, minimum 1). So a level 6 character regains 3 dice. Plan your short rests — you won\'t get all your dice back in one long rest.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'rest-summary',
      target: '[data-tutorial="rest-recovery"]',
      title: 'Recovery Summary',
      message: 'After completing a rest, the green summary panel shows everything that was restored: HP gained, spell slots recovered, Hit Dice regained, and features reset. Review it to confirm your character is ready.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'rest-recovery',
      target: '[data-tutorial="rest-recovery"]',
      title: 'Feature Recovery',
      message: 'Long rest: all spell slots + dawn-recharge items + most class features. Short rest: some features (Action Surge, Second Wind) + Warlock Pact Magic slots. Check your class features to know which reset when.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'rest-time-display',
      target: '[data-tutorial="rest-panel"]',
      title: 'Time Tracking',
      message: 'The panel shows current game time and when you last rested. D&D rules require at least 16 hours between long rests — the system tracks this automatically.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'rest-time',
      target: '[data-tutorial="rest-pass-time"]',
      title: 'Passing Time',
      message: 'Use 1hr/4hr/8hr/day buttons to advance the clock without resting — for travel, shopping, or exploration. Time advances the calendar, and crossing midnight triggers dawn recharges on magic items.',
      type: 'info',
      position: 'top',
      optional: true,
    },
  ],
};

export const BASTION_MODULE = {
  id: 'bastion',
  title: 'Bastion Tour',
  promptMessage: 'Your bastion awaits! Want a tour of the stronghold system?',
  declinable: true,
  steps: [
    {
      id: 'bastion-overview',
      target: '[data-tutorial="bastion-overview"]',
      title: 'Your Bastion',
      message: 'The Bastion system (from D&D 2024) lets you build and manage a personal stronghold. Assign facilities, issue orders, and earn rewards.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'bastion-facilities',
      target: '[data-tutorial="bastion-facilities"]',
      title: 'Facilities',
      message: 'Each facility provides different benefits — a Workshop crafts items, a Library grants research, an Armory stores weapons.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'bastion-orders',
      target: '[data-tutorial="bastion-orders"]',
      title: 'Bastion Orders',
      message: 'Issue orders to your facilities between adventures. Each order takes time and may produce items, gold, or special effects.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'bastion-treasury',
      target: '[data-tutorial="bastion-treasury"]',
      title: 'Treasury',
      message: 'Your bastion has its own treasury. Deposit gold to fund projects, or withdraw it when you need spending money.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'bastion-defenders',
      target: '[data-tutorial="bastion-defenders"]',
      title: 'Defenders',
      message: 'Hire defenders to protect your bastion while you\'re away. They handle threats and keep your facilities safe.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
  ],
};

export const PARTY_MODULE = {
  id: 'party',
  title: 'Party Tour',
  promptMessage: 'Want to learn about party sync?',
  declinable: true,
  steps: [
    {
      id: 'party-create',
      target: '[data-tutorial="party-create"]',
      title: 'Create or Join',
      message: 'Create a new party and share the code, or join an existing one. Party sync lets you see each other\'s characters in real-time.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'party-members',
      target: '[data-tutorial="party-members"]',
      title: 'Party Members',
      message: 'See everyone\'s HP, AC, and conditions at a glance. Great for the DM to track the whole group.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'party-activity',
      target: '[data-tutorial="party-activity"]',
      title: 'Activity Feed',
      message: 'The activity feed shows dice rolls, level-ups, and other actions in real-time across the party.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'party-dm',
      target: '[data-tutorial="party-dm"]',
      title: 'DM Mode',
      message: 'One player can claim the DM role to get extra tools — send items, manage party inventory, and run encounters.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
  ],
};

export const SETTINGS_MODULE = {
  id: 'settings',
  title: 'Settings Tour',
  promptMessage: 'Want a quick look at what you can customize?',
  steps: [
    {
      id: 'settings-display',
      target: '[data-tutorial="settings-display"]',
      title: 'Display Modes',
      message: 'Switch between Default (full features), Simple (streamlined), and Just Inventory (minimal) modes depending on how much complexity you want.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'settings-tabs',
      target: '[data-tutorial="settings-tabs"]',
      title: 'Tab Visibility',
      message: 'Hide tabs you don\'t use to keep the navigation clean. You can always bring them back here.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'settings-rules',
      target: '[data-tutorial="settings-rules"]',
      title: 'Rule Toggles',
      message: 'Customize game rules — exhaustion style (2014 vs 2024), death save persistence, crafting systems. Match your table\'s houserules.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'settings-workshop',
      target: '[data-tutorial="settings-workshop"]',
      title: 'Content Workshop',
      message: 'Create custom content that integrates with the full app. Build homebrew races with custom traits, classes with unique features, or subclasses for existing classes.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    // [ADDED Phase 2B]: How custom content integrates with the rest of the app
    {
      id: 'settings-workshop-use',
      target: '[data-tutorial="settings-workshop"]',
      title: 'Using Custom Content',
      message: 'Everything you create here appears in Character Creation and the Level-Up wizard alongside official content. Custom classes get full spellcasting, features, and proficiency support.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
  ],
};

// [TIER 1.11 — Apr 30 2026]: DOWNTIMES_MODULE expansion. Was 3 steps with 2
// marked optional and steps 2 + 3 sharing the same target. Audit found steps
// 2 and 3 collapsed visually onto each other and the optional flag let
// TutorialSpotlight auto-skip the whole rest of the tutorial when its targets
// were missing. Each step now anchors to a guaranteed-rendered element with
// `downtime-tutorial-anchor` as a fallback, no step is optional, and prose
// explains a non-obvious mechanic. PHB p.187 covers downtime activities,
// XGtE p.123–134 covers the expanded activities (crafting helpers, magic-item
// trade, gambling, pit fighting, religious service, etc.).
export const DOWNTIMES_MODULE = {
  id: 'downtimes',
  title: 'Downtimes Tour',
  promptMessage: 'Want to learn about downtime activities?',
  declinable: true,
  steps: [
    {
      id: 'dt-activities-list',
      target: '[data-tutorial="downtimes-activities"], [data-tutorial="downtime-tutorial-anchor"]',
      title: 'The Downtime Hub',
      message: 'Between adventures, your character can spend in-game days on activities that grant gold, proficiencies, contacts, or recovery instead of XP. Time progresses through the calendar system: every day you advance via the Calendar tab counts toward whatever activities are running. PHB p.187 introduces the downtime concept; XGtE p.123–134 adds the expanded options surfaced in this tab.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'dt-start-button',
      target: '[data-tutorial="downtime-start-button"], [data-tutorial="downtime-tutorial-anchor"]',
      title: 'Picking an Activity',
      message: 'Click "Start New Activity" to open the picker dialog. Activities are grouped into five categories — Work & Earn (Profession, Crafting), Learn & Grow (Training, Researching), Social (Carousing, Religion), Risk & Reward (Crime, Gambling, Pit Fighting), and Commerce & Recovery (Selling/Buying Magic Items, Recuperating). Twelve activities total per the canonical PHB + XGtE catalog.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'dt-configure-form',
      target: '[data-tutorial="downtimes-activities"], [data-tutorial="downtime-tutorial-anchor"]',
      title: 'Configuring an Activity',
      message: 'Once you select a card the Configure panel slides into view. Every activity asks for Duration (days) and Gold Spent; some activities expose extras — Crafting shows a Recipe selector that auto-fills materials cost and computes time at 5gp progress per day, plus an XGtE Helpers field that adds 5gp/workweek per proficient assistant. Gambling asks for a stake, Carousing for a social tier, Selling Magic Items for the item to list. The form recalculates time and cost as you type so the preview always matches what you\'ll actually spend.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'dt-cost-preview',
      target: '[data-tutorial="downtimes-activities"], [data-tutorial="downtime-tutorial-anchor"]',
      title: 'Cost Preview Before You Commit',
      message: 'Below the form a color-coded preview shows total gold cost, days required, and (for crafting) the breakdown formula — for example "100gp materials → 200gp item value ÷ 5gp/day = 40 days". Gold is only deducted from your purse when you click Start Activity in the footer; the dialog won\'t let you start an activity that exceeds your available gold. This prevents accidentally going into debt on a long craft.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'dt-active-progress',
      target: '[data-tutorial="downtimes-tracking"], [data-tutorial="downtime-tutorial-anchor"]',
      title: 'Active Activities & Advancing Time',
      message: 'Started activities appear in the Active Activities list at the top with a progress bar. Click "Advance Time" on a card (or use the Calendar tab) to push days forward — activities tick down automatically. Click "Cancel (50% refund)" to abort early; you get half your gold back per the standard PHB downtime convention. Multiple activities can run in parallel as long as each is plausible (e.g., research while training).',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'dt-collect-rewards',
      target: '[data-tutorial="downtimes-tracking"], [data-tutorial="downtime-tutorial-anchor"]',
      title: 'Collecting Rewards & Activity Log',
      message: 'When the progress bar fills, the card flips to "Collect Rewards" — clicking it rolls the activity outcome (a profession\'s daily wage by tier, a crafted item placed into your inventory, training proficiency learned, gambling result from XGtE\'s 1–5/6–10/11–20/21+ table, etc.) and writes a journal entry summarizing what happened. Every meaningful downtime resolution logs to the activity log so you can audit gold income and proficiency gains across a campaign.',
      type: 'info',
      position: 'bottom',
    },
  ],
};

export const CALENDAR_MODULE = {
  id: 'calendar',
  title: 'Calendar & Journal Tour',
  promptMessage: 'Want a tour of the campaign calendar?',
  steps: [
    {
      id: 'cal-day',
      target: '[data-tutorial="calendar-day"]',
      title: 'Day Tracking',
      message: 'Advance time day by day. The calendar tracks the Faerunian date, season, and moon phase. Dawn triggers magic item recharges.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'cal-season',
      target: '[data-tutorial="calendar-season"]',
      title: 'Seasons & Weather',
      message: 'The calendar shows the current season with color-coded visuals. Seasons can affect travel, encounters, and downtime activities.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'cal-journal',
      // [BATCH 12 / P1 D-20 — APR 2026]: The calendar-journal anchor lives
      //   in JournalTab.js, not CalendarPanel. Pre-fix the spotlight tried
      //   to find it on the calendar tab where it does not exist; the step
      //   silently auto-skipped after 1500ms. The `tab` field below makes
      //   TutorialContext switch to the journal tab before the spotlight
      //   scans the DOM. Per-step `tab` is a clean cross-tab navigation
      //   primitive (sibling to the module-level MODULE_TO_TAB switch).
      tab: 'journal',
      target: '[data-tutorial="calendar-journal"]',
      title: 'Session Journal',
      message: 'Log session notes, important events, and NPC encounters. Entries are tied to the calendar date for easy reference.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
  ],
};

// [TIER 1.11 — Apr 30 2026]: DICE_MODULE expansion. Was 4 steps with 2 marked
// optional and generic prose. Audit found the tutorial closed early (the
// optional dice-tables step targets a div that doesn't exist), and the
// remaining steps explained nothing non-obvious. Each step now anchors to a
// guaranteed-rendered element with `dice-tutorial-anchor` as a fallback, no
// step is optional, and prose explains a non-obvious mechanic with PHB page
// references where useful (PHB p.176 covers dice notation).
export const DICE_MODULE = {
  id: 'dice',
  title: 'Dice & Roll Tables Tour',
  promptMessage: 'Want a tour of the dice roller?',
  steps: [
    {
      id: 'dice-quick-row',
      target: '[data-tutorial="dice-quick-roll"], [data-tutorial="dice-tutorial-anchor"]',
      title: 'Quick Rolls (d4 – d100)',
      message: 'The seven buttons in this row roll a single generic die — d4, d6, d8, d10, d12, d20, and d100 — without applying any character modifier. Use these for raw damage dice, percentile rolls, hit-die spends, or any time the DM calls for "roll a d20" without a bonus. PHB p.176 explains dice notation if NdX (e.g., 2d6) is unfamiliar.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'dice-character-rolls',
      target: '[data-tutorial="dice-character-rolls"], [data-tutorial="dice-tutorial-anchor"]',
      title: 'Character Rolls (Bonuses Applied)',
      message: 'This panel surfaces one-click bonus-applied 1d20 rolls for the most common D&D actions: Initiative, all six Saving Throws, and the six most-used skills (Perception, Investigation, Stealth, Athletics, Acrobatics, Insight). The shown formula already includes your ability mod, proficiency, expertise, item bonuses, and active feat effects (Alert adds +5 to Initiative, for example) — the math is done for you.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'dice-common-rolls',
      target: '[data-tutorial="dice-common-rolls"], [data-tutorial="dice-tutorial-anchor"]',
      title: 'Common D&D Rolls + Equipped Weapons',
      message: 'These presets cover frequent raw rolls: ability checks, sneak attack dice, healing potion dice, and so on — no character modifier added because the DM may want a clean die. If you have a weapon equipped to your main-hand or off-hand, its damage formula is prepended to this row automatically (a +1 longsword shows "1d8+1" labeled with the weapon name), so you can roll exactly what you swing.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'dice-custom-formula',
      target: '[data-tutorial="dice-custom-formula"], [data-tutorial="dice-tutorial-anchor"]',
      title: 'Custom Formula Input',
      message: 'Type any dice expression — 2d6+4, 4d8+5, 1d20-2 — and press Enter or click Roll. Use the Roll Mode buttons above (Normal / Advantage / Disadvantage) to roll twice and take higher or lower; that toggle only affects 1d20 rolls per RAW. Click the star next to the input to save a formula as a favorite for one-click re-rolling later.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'dice-history',
      target: '[data-tutorial="dice-history-toggle"], [data-tutorial="dice-tutorial-anchor"]',
      title: 'Roll History & Statistics',
      message: 'Every roll — generic, character, custom, or auto-rolled by the app (skill checks, attacks) — lands in the Roll History panel for the last 100 entries. Toggle the Statistics view to see total rolls, average, highest, lowest, and your natural 20 / natural 1 rates. Sort by newest, oldest, highest, or lowest, or remove a single entry with the trash icon.',
      type: 'info',
      position: 'top',
    },
    {
      id: 'dice-active-bonuses',
      target: '[data-tutorial="dice-tutorial-anchor"]',
      title: 'Active Damage Bonuses',
      message: 'When you toggle conditional damage features in Quick Actions → Combat Toggles (Sneak Attack, Hexblade\'s Curse, Improved Divine Smite, Hunter Colossus Slayer, Champion Improved Critical extra crit dice, etc.), an Active Damage Bonuses panel appears between Quick Roll and Character Rolls listing each active bonus. The composite roll button rolls all active dice in one go so you don\'t have to remember which features stack.',
      type: 'info',
      position: 'bottom',
    },
  ],
};

// ========================================
// DIALOG TUTORIAL MODULES (prompted on dialog open)
// ========================================

// ========================================
// CUSTOM SPELL CREATOR MODULE
// ========================================
// [PURPOSE]: Walks through the 44+ form controls in the Custom Spell Creator dialog
// [WHY]: 980-line dialog with nested components, upcast scaling, dual damage types — zero guidance existed
export const CUSTOM_SPELL_MODULE = {
  id: 'custom-spell',
  title: 'Custom Spell Creator',
  promptMessage: 'Want a quick walkthrough of the spell creator?',
  steps: [
    {
      id: 'cspell-overview',
      target: '[data-tutorial="custom-spell-header"]',
      title: 'Custom Spell Creator',
      message: 'Build homebrew spells that fully integrate with the casting system. You can also click "Use Template" to copy an existing SRD spell and modify it — great for reskinning official spells.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'cspell-name-level',
      target: '[data-tutorial="custom-spell-basics"]',
      title: 'Name, Level & School',
      message: 'Name is required. Level ranges from Cantrip (0) to 9th. School of magic (Evocation, Abjuration, etc.) affects some class features — Evoker Wizards get bonuses to Evocation spells, for example.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'cspell-ritual-conc',
      target: '[data-tutorial="custom-spell-ritual-conc"]',
      title: 'Ritual & Concentration',
      message: 'Ritual: can be cast without a spell slot but takes 10 extra minutes (only some classes can ritual cast). Concentration: spell is maintained and ends if you take damage and fail a CON save, or cast another concentration spell.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'cspell-casting-props',
      target: '[data-tutorial="custom-spell-casting-props"]',
      title: 'Casting Properties',
      message: 'Casting time (1 action, bonus action, reaction, or longer), range (self to 300ft+), and duration (instantaneous to 24 hours). Most combat spells are 1 action with instantaneous duration.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'cspell-components',
      target: '[data-tutorial="custom-spell-components"]',
      title: 'Spell Components',
      message: 'V (Verbal) = must speak, S (Somatic) = must gesture, M (Material) = requires a physical item. Checking M reveals the material description field and the Component Details panel below it.',
      type: 'info',
      position: 'top',
      optional: true,
    },
    {
      id: 'cspell-material-details',
      target: '[data-tutorial="custom-spell-material-details"]',
      title: 'Material Component Details',
      message: 'Set a GP cost for the material and whether it\'s consumed on casting. "Quick Create Component" lets you name the item, set its cost, and automatically add it to your inventory — no need to create it separately.',
      type: 'info',
      position: 'top',
      optional: true,
      waitForTarget: true,
    },
    {
      id: 'cspell-description',
      target: '[data-tutorial="custom-spell-description"]',
      title: 'Spell Description',
      message: 'Required field — describe the full effect of the spell. This is what players see when they view the spell details. Be as detailed as official spells: target selection, area of effect, duration behavior, and edge cases.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'cspell-damage',
      target: '[data-tutorial="custom-spell-damage"]',
      title: 'Damage, Healing & Saves',
      message: 'Set primary damage dice (e.g., 8d6) and type (fire, cold, etc.). Click the green heart to toggle healing mode. A secondary damage row appears after adding primary damage — for spells like Booming Blade. Choose spell attack roll vs saving throw.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'cspell-upcast',
      target: '[data-tutorial="custom-spell-upcast"]',
      title: 'Upcasting & Dice Scaling',
      message: 'The text field describes non-dice upcast effects (extra targets, longer duration). The "Automatic Dice Scaling" panel adds extra dice per level above base — set the dice, damage type, and starting level. Hidden for cantrips (they scale differently).',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'cspell-classes',
      target: '[data-tutorial="custom-spell-classes"]',
      title: 'Class Spell Lists',
      message: 'Check which of the 8 casting classes can learn this spell. Spells with no classes selected only appear under "Homebrew" in the spell browser — useful for NPC-only spells or DM tools.',
      type: 'info',
      position: 'top',
    },
    {
      id: 'cspell-preview',
      target: '[data-tutorial="custom-spell-preview"]',
      title: 'Live Spell Preview',
      message: 'This preview updates in real-time as you fill out the form. It shows exactly how the spell will appear in your spellbook — verify the details look correct before saving.',
      type: 'info',
      position: 'top',
      optional: true,
    },
    {
      id: 'cspell-save-source',
      target: '[data-tutorial="custom-spell-save-source"]',
      title: 'Save to Source',
      message: 'Check "Save to Source" and pick a custom content source to make this spell reusable across all your characters. Without this, the spell only exists in the current character\'s spellbook.',
      type: 'info',
      position: 'top',
      optional: true,
    },
  ],
};

// ========================================
// ADD ITEM DIALOG MODULE
// ========================================
// [PURPOSE]: Guides users through the multi-stage Add Item dialog
// [WHY]: Multi-stage flow (search → category browse → customization) with 50+ advanced properties
export const ADD_ITEM_MODULE = {
  id: 'add-item',
  title: 'Add Item Guide',
  promptMessage: 'Want a quick guide to adding items?',
  steps: [
    {
      id: 'additem-search',
      target: '[data-tutorial="add-item-search"]',
      title: 'Search the Item Database',
      message: 'Fuzzy search across all D&D items — type partial names like "lng sw" to find Longsword. Results show category, size, and value. The star icon saves items to your favorites for quick access.',
      type: 'info',
      position: 'bottom',
    },
    {
      id: 'additem-favorites',
      target: '[data-tutorial="add-item-favorites"]',
      title: 'Favorites & Recent Items',
      message: 'Your starred favorites and recently added items appear here for quick re-adding. Great for consumables like arrows or potions that you buy frequently.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'additem-browse',
      target: '[data-tutorial="add-item-browse"]',
      title: 'Browse by Category',
      message: 'Opens a two-stage browser: first pick a category (Weapons, Armor, Gems, etc.), then pick an item within it. Useful when you\'re not sure what you\'re looking for or want to see everything available.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'additem-options',
      target: '[data-tutorial="add-item-options"]',
      title: 'Item Details & Category Options',
      message: 'After selecting an item, category-specific options appear. Gems show tier buttons (10gp to 5,000gp), trade goods have quantity multipliers, and containers display their grid dimensions and magical properties.',
      type: 'info',
      position: 'bottom',
      waitForTarget: true,
    },
    {
      id: 'additem-customize',
      target: '[data-tutorial="add-item-customize"]',
      title: 'Name & Value Overrides',
      message: 'Give the item a custom name (e.g., "Flame Tongue" instead of "Longsword"), description, and override the default weight or value. Leave blank to use the base item\'s defaults.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'additem-equip-slot',
      target: '[data-tutorial="add-item-equip-slot"]',
      title: 'Equipment Slots & Attunement',
      message: '16 equipment positions (head, chest, main hand, ring, etc.). Setting a slot makes the item equippable — drag it to your equipment panel later. When a slot is set, the Attunement checkbox appears for magic items that require it.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'additem-advanced',
      target: '[data-tutorial="add-item-advanced"]',
      title: 'Advanced Magic Item Properties',
      message: 'Expand "Advanced Properties" for magic enhancement (+1/+2/+3), special materials (Adamantine ignores crits, Mithral removes STR requirement, Silvered bypasses lycanthrope resistance), and rarity classification.',
      type: 'info',
      position: 'top',
      optional: true,
    },
    {
      id: 'additem-charges-spells',
      target: '[data-tutorial="add-item-charges-spells"]',
      title: 'Charges & Granted Spells',
      message: 'Set current/max charges and a recharge type (dawn, short rest, consumable, etc.). Attach spells from the database that the item can cast — set uses per day for each. A Staff of Healing might grant Cure Wounds 3/day.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'additem-defensive',
      target: '[data-tutorial="add-item-defensive"]',
      title: 'Defensive & Sensory Properties',
      message: 'Grant damage resistance/immunity (fire, cold, etc.), speed bonuses, darkvision/tremorsense, and condition immunities. All applied automatically when the item is equipped and attuned.',
      type: 'info',
      position: 'bottom',
      optional: true,
    },
    {
      id: 'additem-free',
      target: '[data-tutorial="add-item-free"]',
      title: 'Buy for Free',
      message: 'Check this to bypass the gold cost entirely. Perfect for quest rewards, DM gifts, found loot, or starting equipment — any item that shouldn\'t cost the character money.',
      type: 'info',
      position: 'top',
    },
    {
      id: 'additem-save-source',
      target: '[data-tutorial="add-item-save-source"]',
      title: 'Save to Source',
      message: 'Check "Save to Source" and select a custom content source to make this item permanently available across all your characters. Without this, you\'d need to recreate it manually each time.',
      type: 'info',
      position: 'top',
      optional: true,
    },
  ],
};

// ========================================
// DM TUTORIAL MODULE (NEW — Session 11)
// ========================================
// [PURPOSE]: Walks DMs through the tools they unlock — vault, shop, downtime, bastion, DM push.
// [WHY]: DMs are the most engaged users but had no dedicated onboarding. Triggered from the
//        WelcomePopup "I'm the DM" button and the post-premium-purchase DM popup.
// [TARGET STRATEGY]: All steps target [data-tutorial="dm-tutorial-anchor"] — a fixed invisible
//        anchor injected by App.jsx. This guarantees the tutorial panel renders on any tab and
//        on mobile (where tab nav buttons for secondary tabs live inside the "More" drawer).
export const DM_TUTORIAL_MODULE = {
  id: 'dm-tutorial',
  title: 'DM Tools Tour',
  promptMessage: 'Want a walkthrough of the DM tools?',
  declinable: true,
  steps: [
    {
      id: 'dm-seat',
      target: '[data-tutorial="dm-tutorial-anchor"]',
      title: 'The DM Seat',
      message: 'You run the game. The DM seat unlocks tools the whole table needs — shared vault, shop generator, downtime manager, and a bastion to call home. Most live on their own tabs; the Party tab is your DM command center.',
      type: 'info',
      position: 'bottom',
      waitForTarget: true,
    },
    {
      id: 'dm-vault',
      target: '[data-tutorial="dm-tutorial-anchor"]',
      title: 'The Party Vault',
      message: 'Shared loot lives in the Party Vault. To share an item: long-press (or right-click) any inventory item and choose "Add to Party Vault." Anyone in the party can then claim it from the vault on the Party tab.',
      type: 'info',
      position: 'bottom',
      waitForTarget: true,
    },
    {
      id: 'dm-shop',
      target: '[data-tutorial="dm-tutorial-anchor"]',
      title: 'Building a Shop',
      message: 'The Shop tab generates randomized shops by settlement type — hamlet, town, or city. Pick a shop type (blacksmith, apothecary, general store) and it stocks with period-appropriate gear. Push a shop to the party so they can browse together.',
      type: 'info',
      position: 'bottom',
      waitForTarget: true,
    },
    {
      id: 'dm-downtime',
      target: '[data-tutorial="dm-tutorial-anchor"]',
      title: 'Running Downtime',
      message: 'Between sessions, characters can craft, research, or carouse. The Downtime tab tracks each activity\'s days, costs, and outcomes — perfect for long campaigns where weeks pass between sessions.',
      type: 'info',
      position: 'bottom',
      waitForTarget: true,
    },
    {
      id: 'dm-bastion',
      target: '[data-tutorial="dm-tutorial-anchor"]',
      title: 'Bastion Management',
      message: 'Build and upgrade a stronghold using the D&D 2024 Bastion rules. Assign facilities, issue orders between adventures, and earn rewards. A great late-campaign goal for heroes who\'ve earned a place to call home.',
      type: 'info',
      position: 'bottom',
      waitForTarget: true,
    },
    {
      id: 'dm-push',
      target: '[data-tutorial="dm-tutorial-anchor"]',
      title: 'Passing Items & Notes',
      message: 'From the Party tab\'s DM tools, push shops, loot rolls, calendar sync, and journal notes to your players in real-time. No copying, no forwarding — the whole table stays in sync automatically.',
      type: 'info',
      position: 'bottom',
      waitForTarget: true,
    },
  ],
};

// ========================================
// MODULE REGISTRY
// ========================================
// [PURPOSE]: Central lookup for all tutorial modules
// [WHY]: TutorialContext iterates this to manage state and trigger prompts

export const TUTORIAL_MODULES = {
  'welcome': WELCOME_MODULE,
  'character-creation': CHARACTER_CREATION_MODULE,
  'character': CHARACTER_MODULE,
  'shop': SHOP_MODULE,
  'inventory': INVENTORY_MODULE,
  'spellbook': SPELLBOOK_MODULE,
  'skills': SKILLS_MODULE,
  'feats': FEATS_MODULE,
  'rest': REST_MODULE,
  'bastion': BASTION_MODULE,
  'party': PARTY_MODULE,
  'settings': SETTINGS_MODULE,
  'downtimes': DOWNTIMES_MODULE,
  'calendar': CALENDAR_MODULE,
  'dice': DICE_MODULE,
  // Dialog tutorials (triggered by promptIfNeeded, not tab visits)
  'custom-spell': CUSTOM_SPELL_MODULE,
  'add-item': ADD_ITEM_MODULE,
  // [S11]: DM onboarding — triggered from WelcomePopup "I'm the DM" + post-purchase popup
  'dm-tutorial': DM_TUTORIAL_MODULE,
};

// ========================================
// TAB-TO-MODULE MAPPING
// ========================================
// [PURPOSE]: Maps MobileNavigation tab IDs to tutorial module IDs
// [WHY]: When a tab is visited for the first time, we look up which tutorial to prompt

export const TAB_TO_MODULE = {
  'character': 'character',
  'shop': 'shop',
  'inventory': 'inventory',
  'spellbook': 'spellbook',
  'skills': 'skills',
  'feats': 'feats',
  'rest': 'rest',
  'bastion': 'bastion',
  'party': 'party',
  'settings': 'settings',
  'downtimes': 'downtimes',
  'calendar': 'calendar',
  // [FIX MAY 2026]: Journal no longer auto-fires the calendar tutorial. The
  // combined Calendar & Journal Tour still includes a journal step (cal-journal)
  // and TutorialContext switches tabs per-step, but auto-prompting only on the
  // Calendar tab keeps users from seeing a "calendar tutorial" when they open
  // Journal. A dedicated JOURNAL_MODULE can be added later if needed.
  'dice': 'dice',
  'tables': 'dice',       // Roll tables shares the dice module
};

/**
 * [FIX APR 2026 Phase 3C.1]: Inverse mapping — which tab should be active when
 * a given tutorial module starts. Used to auto-switch to the tutorial's home tab
 * before the spotlight searches for DOM targets. Without this, a tutorial started
 * from an unrelated tab showed only grey-screen spotlight cutouts because its
 * data-tutorial selectors weren't mounted.
 *
 * Note: 'calendar' module maps to 'calendar' tab (not 'journal' — primary home).
 *       'dice' module maps to 'dice' tab (not 'tables' — primary home).
 */
export const MODULE_TO_TAB = {
  character: 'character',
  shop: 'shop',
  inventory: 'inventory',
  spellbook: 'spellbook',
  skills: 'skills',
  feats: 'feats',
  rest: 'rest',
  bastion: 'bastion',
  party: 'party',
  settings: 'settings',
  downtimes: 'downtimes',
  calendar: 'calendar',
  dice: 'dice',
  // Wizard / welcome tutorials aren't tab-scoped — omit to skip auto-switch
};

// [v1.1]: Premium-only tutorial modules — skip for free users
// [S11]: 'dm-tutorial' is premium-gated — free users never see DM onboarding content
export const PREMIUM_TUTORIAL_MODULES = new Set(['shop', 'bastion', 'downtimes', 'party', 'dm-tutorial']);

// ========================================
// TAB OVERVIEW DATA (for WelcomePopup)
// ========================================
// [PURPOSE]: Brief descriptions of each tab for the welcome screen
// [WHY]: Helps new users understand what's available before they start

export const TAB_DESCRIPTIONS = [
  { id: 'character', icon: 'User', label: 'Character', desc: 'Your character sheet — stats, HP, AC, and class features.' },
  { id: 'inventory', icon: 'Package', label: 'Inventory', desc: 'Grid-based inventory with drag-and-drop and equipment slots.' },
  { id: 'spellbook', icon: 'Book', label: 'Spellbook', desc: 'Manage spells, spell slots, and casting — 343 spells included.' },
  { id: 'skills', icon: 'Award', label: 'Skills', desc: 'Roll skill checks, tool proficiencies, and saving throws.' },
  { id: 'feats', icon: 'Star', label: 'Feats', desc: 'Track feats and their passive bonuses.' },
  { id: 'dice', icon: 'Dices', label: 'Dice', desc: 'Full dice roller with custom formulas and history.' },
  { id: 'rest', icon: 'Moon', label: 'Rest', desc: 'Short and long rests with Hit Dice and slot recovery.' },
  { id: 'shop', icon: 'Coins', label: 'Shop', desc: 'Browse and buy from generated D&D shops.' },
  { id: 'party', icon: 'Shield', label: 'Party', desc: 'Real-time party sync with shared activity feed.' },
  { id: 'bastion', icon: 'Castle', label: 'Bastion', desc: 'Build and manage your D&D 2024 stronghold.' },
  { id: 'calendar', icon: 'Calendar', label: 'Calendar', desc: 'Track campaign time with the Faerunian calendar.' },
  { id: 'journal', icon: 'ScrollText', label: 'Journal', desc: 'Session notes, NPC logs, and quest tracking.' },
  { id: 'settings', icon: 'Settings', label: 'Settings', desc: 'Display modes, rule toggles, and the Content Workshop.' },
];
